<template>
  <component
    :is="component"
    :class="[
      { enable: enable },
      size,
      `theme-color-${theme}`,
      'stroke-' + stroke,
    ]"
    v-bind="props"
  >
    <Icon :size="iconSize" class="icon" :name="iconName" />
  </component>
</template>
<script setup lang="ts">
import type { NuxtLinkProps } from '#app';
import type { RouteLocationRaw } from 'vue-router';
interface Props extends NuxtLinkProps {
  iconName: string;
  enable?: boolean;
  stroke?: '0' | 's' | 'n' | 'm' | 'l';
  size?: 'normal' | 'small' | 'x-small';
  iconSize?: string;
  theme:
    | 'primary'
    | 'neutral'
    | 'secondary'
    | 'secondary-reverse'
    | 'secondary-light'
    | 'light'
    | 'warning'
    | 'negative';
  to?: string | RouteLocationRaw;
}
const props = defineProps({
  iconName: null,
  enable: { type: Boolean, default: true },
  stroke: { default: 'l' },
  size: { default: 'normal' },
  iconSize: { default: '1.4rem' },
  theme: { default: 'primary' },
  to: null
});
const component = computed(() => {
  if (props.to || props.href) {
    return resolveComponent('NuxtLink');
  }
  return 'button';
});
</script>
<style lang="scss" scoped>
button,
a {
  --color_icon: var(--color_neutral_200);
  --color_background: var(--color_neutral_60);
  --color_border: var(--color_neutral_60);

  display: flex;
  align-items: center;
  justify-content: center;
  width: 4.6rem;
  height: 4.6rem;
  padding: 0 0.4rem;
  transition: color 400ms $ease-out-quad, border 400ms $ease-out-quad,
    background-color 400ms $ease-out-quad;
  border-radius: 50%;

  // border: var(--stroke_thickness_l) solid var(--color_icon);
  border-color: var(--color_border);
  background: var(--color_background);
  color: var(--color_icon);
  pointer-events: none;
  &.enable {
    border-color: var(--color_border);
    pointer-events: auto;

    &.theme-color-neutral {
      --color_icon: var(--color_neutral_600);
      --color_background: var(--color_neutral_50);
      --color_border: var(--color_neutral_100);
    }

    &.theme-color-warning {
      --color_icon: var(--color_neutral_600);
      --color_background: var(--color_neutral_50);
      --color_border: var(--color_neutral_100);
    }

    &.theme-color-secondary {
      --color_icon: var(--color_secondary);
      --color_background: var(--color_neutral_25);
      --color_border: var(--color_secondary);
    }
    &.theme-color-secondary-reverse {
      --color_icon: var(--color_neutral_25);
      --color_background: var(--color_secondary);
      --color_border: var(--color_neutral_25);
    }

    &.theme-color-secondary-light {
      --color_icon: var(--color_neutral_700);
      --color_background: var(--color_secondary_light);
      --color_border: var(--color_secondary_light);
    }

    &.theme-color-light {
      --color_icon: var(--color_neutral_900);
      --color_background: var(--color_secondary_light);
      --color_border: var(--color_secondary_light);
    }

    &.theme-color-negative {
      --color_icon: var(--color_neutral_900);
      --color_background: var(--color_neutral_25);
      --color_border: var(--color_neutral_25);
    }

    --color_icon: var(--color_primary);
    --color_background: var(--color_neutral_50);
    --color_border: var(--color_primary);
    &:hover,
    &:focus {
      transition: none;
      border-color: var(--color_background);

      --color_icon: var(--color_neutral_100);
      --color_background: var(--color_primary);
      --color_border: var(--color_neutral_100);
      &.theme-color-neutral {
        --color_icon: var(--color_neutral_50);
        --color_background: var(--color_neutral_600);
        --color_border: var(--color_neutral_50);
      }

      &.theme-color-warning {
        --color_icon: var(--color_neutral_50);
        --color_background: var(--color_warning);
        --color_border: var(--color_neutral_50);
      }

      &.theme-color-secondary {
        --color_icon: var(--color_neutral_25);
        --color_background: var(--color_secondary);
        --color_border: var(--color_neutral_25);
      }
      &.theme-color-secondary-reverse {
        --color_icon: var(--color_secondary);
        --color_background: var(--color_neutral_25);
        --color_border: var(--color_secondary);
      }
      &.theme-color-secondary-light {
        --color_icon: var(--color_neutral_25);
        --color_background: var(--color_secondary);
        --color_border: var(--color_secondary);
      }

      &.theme-color-light,
      &.theme-color-negative {
        --color_icon: var(--color_neutral_25);
        --color_background: var(--color_secondary);
        --color_border: var(--color_secondary);
      }
    }
  }

  &.small {
    width: 3.6rem;
    height: 3.6rem;
  }

  &.x-small {
    width: 2.8rem;
    height: 2.8rem;
  }
}
</style>
